import React from 'react';
import type { ReactNode } from 'react';
import { generatePath } from 'react-router-dom';
import { Dashboard } from 'pages/Dashboard';
import { Proposals } from 'pages/Proposals';
import { Invoices } from 'pages/Invoices';
import { Demos } from 'pages/Demos';
import { type TLayouts } from 'app/features/Layout/LayoutTypes';
import { Items, MoodBoard, MoodBoards } from 'app/pages';

interface IRouteParam {
	[key: string]: string | number;
}

interface IRoute {
	path: string;
	element: ReactNode;
	protected: boolean;
	getRoute: (params: IRouteParam) => string;
	layout?: TLayouts;
	icon?: string;
	name: string;
}

interface IRoutes {
	[key: string]: IRoute;
}

type TBaseRoutes = {
	[key: string]: Omit<IRoute, 'getRoute'>;
};

//   Note: If a route doesn't have a DefaultLayout, then it will require a manual layout within the page component.
const baseRoutes: TBaseRoutes = {
	dashboard: {
		path: 'dashboard',
		element: <Dashboard />,
		protected: true,
		layout: 'default',
		icon: 'rectangle-group',
		name: 'Dashboard',
	},
	demo: {
		path: 'demos',
		element: <Demos />,
		protected: true,
		icon: 'rectangle-group',
		name: 'Demos',
	},
	items: {
		path: 'items',
		element: <Items />,
		protected: true,
		icon: 'queue-list',
		name: 'Items',
	},
	proposals: {
		path: 'proposals',
		layout: 'default',
		element: <Proposals />,
		protected: true,
		icon: 'document-duplicate',
		name: 'Proposals',
	},
	moodboards: {
		path: 'moodboards',
		layout: 'default',
		element: <MoodBoards />,
		protected: true,
		icon: 'square-3-stack-3d',
		name: 'Mood Boards',
	},
	// Read-only view of a single board. Not a nav item — it is reached by
	// clicking a card on the Mood Boards list.
	moodboard: {
		path: 'moodboards/:id',
		layout: 'default',
		element: <MoodBoard />,
		protected: true,
		icon: 'square-3-stack-3d',
		name: 'Mood Board',
	},
	invoices: {
		path: 'invoices',
		element: <Invoices />,
		protected: true,
		icon: 'inbox-arrow-down',
		name: 'Invoices',
	},
};

export const routes: IRoutes = {} as IRoutes;

Object.keys(baseRoutes).forEach((routeName) => {
	routes[routeName] = {
		...baseRoutes[routeName],
		getRoute: (params: IRouteParam) => {
			const path = generatePath(baseRoutes[routeName].path, params);
			return path;
		},
	};
});
